import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Edit2, UserMinus, RefreshCcw, KeyRound, Copy, Trash2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { ASSIGNMENT_OPTIONS } from "@/lib/yasamaru";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AssignmentManagement from "@/pages/admin/AssignmentManagement";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ROLE_LABEL = { admin: "管理者", manager: "担当者", staff: "スタッフ" };

const EMPTY = {
  name: "", login_id: "", email: "", phone: "", password: "",
  role: "staff", department_id: "", position_id: "", manager_id: "",
  employment_type: "パート", is_active: true,
  monthly_desired_days: "", weekly_max_days: "",
  capable_assignments: [],
};

export default function StaffManagement() {
  const { user: actor } = useAuth();
  const isManager = actor?.role === "manager";
  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    api.get("/assignments").then((r) => setAssignments(r.data.filter((a) => a.is_active).map((a) => a.name))).catch(() => setAssignments(ASSIGNMENT_OPTIONS));
  }, []);

  // Manager: show only own staff
  const visibleUsers = isManager
    ? users.filter((u) => u.role === "staff" && u.manager_id === actor?.id)
    : users;

  // (removed managers filter — UI no longer shows manager assignment dropdown)
  const managers = users.filter((u) => u.role === "manager");

  const save = async () => {
    try {
      const payload = { ...form };
      const passwordEntered = form.password; // 新規作成時にダイアログ表示用に保持
      if (!payload.password) delete payload.password;
      // Strip fields not editable in simplified UI
      delete payload.email;
      delete payload.phone;
      delete payload.employment_type;
      delete payload.department_id;
      delete payload.position_id;
      delete payload.monthly_desired_days;
      delete payload.weekly_max_days;
      // manager_id: keep when admin sets it; manager role's create call auto-assigns server-side
      if (payload.manager_id === "") payload.manager_id = null;
      if (editing) {
        await api.put(`/users/${editing.id}`, payload);
        toast.success("保存しました");
      } else {
        if (!form.password) {
          toast.error("初回登録時はパスワードが必要です");
          return;
        }
        const { data } = await api.post("/users", payload);
        toast.success("スタッフを登録しました");
        // 新規作成 staff の場合のみ、ログイン情報配布ダイアログを表示
        if (data?.role === "staff") {
          setPwResetResult({
            login_id: data.login_id,
            password: passwordEntered,
            display_name: data.display_name || data.name,
            isNew: true,
          });
        }
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const startEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name, login_id: u.login_id, email: u.email || "", phone: u.phone || "", password: "",
      role: u.role, department_id: u.department_id || "", position_id: u.position_id || "",
      manager_id: u.manager_id || "", employment_type: u.employment_type || "パート",
      is_active: u.is_active,
      monthly_desired_days: u.monthly_desired_days ?? "",
      weekly_max_days: u.weekly_max_days ?? "",
      capable_assignments: u.capable_assignments || [],
    });
    setOpen(true);
  };

  const [confirmDeactivate, setConfirmDeactivate] = useState(null); // user obj
  const [confirmHardDelete, setConfirmHardDelete] = useState(null); // user obj (完全削除確認)
  const [pwResetTarget, setPwResetTarget] = useState(null); // ユーザー (確認段階)
  const [pwResetResult, setPwResetResult] = useState(null); // {login_id, password, display_name}

  const doResetPassword = async () => {
    const u = pwResetTarget;
    if (!u) return;
    setPwResetTarget(null);
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setPwResetResult(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyPw = () => {
    if (!pwResetResult?.password) return;
    navigator.clipboard.writeText(pwResetResult.password).then(
      () => toast.success("パスワードをコピーしました"),
      () => toast.error("コピーに失敗しました"),
    );
  };

  // LINE 用の案内文を組み立て
  const buildLineMessage = () => {
    if (!pwResetResult) return "";
    const companyId = actor?.login_id || "(企業ログインID)";
    const loginUrl = "https://shiftmagic.jp/login";
    const name = pwResetResult.display_name || pwResetResult.login_id;
    return [
      `${name} さん、やさまるシフトのログイン情報です。`,
      "",
      "▼ ログインページ",
      loginUrl,
      "",
      "▼ ログイン情報（スタッフ用タブ）",
      `企業ID: ${companyId}`,
      `個人ID: ${pwResetResult.login_id}`,
      `パスワード: ${pwResetResult.password}`,
      "",
      "▼ 使い方",
      "・希望シフトはこのページから登録できます。",
      "・確定したシフトもこちらから確認できます。",
      "・人が足りない日などにお願い依頼が届くこともあるので、確認お願いします！",
      "",
      "わからないことがあれば気軽に連絡してください。",
    ].join("\n");
  };

  const copyLineMessage = () => {
    const text = buildLineMessage();
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => toast.success("LINE用の文をコピーしました"),
      () => toast.error("コピーに失敗しました"),
    );
  };

  const requestDeactivate = (u) => {
    setOpen(false);
    setConfirmDeactivate(u);
  };

  const doDeactivate = async () => {
    const u = confirmDeactivate;
    if (!u) return;
    setConfirmDeactivate(null);
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("退職扱いにしました");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const requestHardDelete = (u) => {
    setOpen(false);
    setConfirmDeactivate(null);
    setConfirmHardDelete(u);
  };

  const doHardDelete = async () => {
    const u = confirmHardDelete;
    if (!u) return;
    setConfirmHardDelete(null);
    try {
      await api.delete(`/users/${u.id}?hard=true`);
      toast.success("ユーザーを完全に削除しました");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reactivate = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { is_active: true });
      toast.success("在籍中に戻しました");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="staff-management-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">スタッフ管理</h1>
      </div>

      <Tabs defaultValue="staff" className="w-full">
        <TabsList className="mb-2">
          <TabsTrigger value="staff" data-testid="tab-staff">スタッフ</TabsTrigger>
          <TabsTrigger value="assignments" data-testid="tab-assignments">配置マスタ</TabsTrigger>
        </TabsList>

        <TabsContent value="staff" className="space-y-3">
          <div className="flex justify-end">
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(EMPTY); } }}>
              <DialogTrigger asChild>
                <Button data-testid="add-user-button" className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-1" />新規登録
                </Button>
              </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? "編集" : "新規登録"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">氏名 (または ニックネーム)</Label>
                <Input data-testid="user-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="例: 山田 太郎 / たろう" />
              </div>
              <div>
                <Label className="text-xs">ログインID</Label>
                <Input data-testid="user-login-id" value={form.login_id} onChange={(e) => setForm({...form, login_id: e.target.value})} />
              </div>
              <div>
                <Label className="text-xs">パスワード {editing && "(変更時のみ)"}</Label>
                <Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} />
              </div>
              {!isManager && (
                <div>
                  <Label className="text-xs">権限</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                    <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">管理者</SelectItem>
                      <SelectItem value="manager">担当者</SelectItem>
                      <SelectItem value="staff">スタッフ</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {form.role === "staff" && !isManager && (
                <div className="col-span-2">
                  <Label className="text-xs">担当者 (管轄するマネージャー)</Label>
                  <Select value={form.manager_id || "_none"} onValueChange={(v) => setForm({...form, manager_id: v === "_none" ? "" : v})}>
                    <SelectTrigger data-testid="user-manager"><SelectValue placeholder="未設定" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">未設定</SelectItem>
                      {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="col-span-2 flex items-center gap-2">
                <input id="is-active" type="checkbox" data-testid="user-active" checked={form.is_active} onChange={(e) => setForm({...form, is_active: e.target.checked})} />
                <label htmlFor="is-active" className="text-sm">在籍中</label>
              </div>

              {form.role === "staff" && (
                <div className="col-span-2 border-t border-slate-200 pt-3 mt-1">
                  <Label className="text-xs font-semibold text-slate-700">対応できる配置 (担当者が設定)</Label>
                  <p className="text-[10px] text-slate-500 mt-0.5 mb-2">月間希望日数・週間上限はスタッフ本人が初回設定で入力します。</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(assignments.length ? assignments : ASSIGNMENT_OPTIONS).map((a) => {
                      const selected = form.capable_assignments.includes(a);
                      return (
                        <button
                          key={a}
                          type="button"
                          data-testid={`capable-${a}`}
                          onClick={() => {
                            const next = selected
                              ? form.capable_assignments.filter((x) => x !== a)
                              : [...form.capable_assignments, a];
                            setForm({ ...form, capable_assignments: next });
                          }}
                          className={`text-xs px-2.5 py-1 rounded-full border-2 ${
                            selected
                              ? "bg-blue-50 text-blue-700 border-blue-400"
                              : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          {a}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter className="flex flex-row justify-between items-center sm:justify-between gap-2">
              <div className="flex gap-2 flex-wrap">
                {editing && editing.is_active && (
                  <Button
                    variant="outline"
                    className="text-rose-700 border-rose-200 hover:bg-rose-50"
                    onClick={() => requestDeactivate(editing)}
                    data-testid={`deactivate-${editing.id}`}
                  >
                    <UserMinus className="w-4 h-4 mr-1" />退職扱いにする
                  </Button>
                )}
                {editing && !editing.is_active && (
                  <Button
                    variant="outline"
                    className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                    onClick={async () => { setOpen(false); await reactivate(editing); }}
                    data-testid={`reactivate-${editing.id}`}
                  >
                    <RefreshCcw className="w-4 h-4 mr-1" />在籍中に戻す
                  </Button>
                )}
                {editing && (editing.role === "staff") && (
                  <Button
                    variant="outline"
                    className="text-rose-800 border-rose-300 bg-rose-50/40 hover:bg-rose-100 hover:text-rose-900"
                    onClick={() => requestHardDelete(editing)}
                    data-testid={`hard-delete-${editing.id}`}
                    title="誤って登録した場合に完全削除します(履歴も削除)"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />完全削除
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>キャンセル</Button>
                <Button data-testid="save-user-button" onClick={save} className="bg-blue-600 hover:bg-blue-700">保存</Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
          </div>

      <Card className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-slate-600 border-b border-slate-200">
              <th className="px-3 py-2">氏名</th>
              <th className="px-3 py-2">ログインID</th>
              <th className="px-3 py-2">権限</th>
              <th className="px-3 py-2">対応できる配置</th>
              <th className="px-3 py-2">状態</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {visibleUsers.map((u) => (
              <tr key={u.id} className="border-b border-slate-100" data-testid={`user-row-${u.id}`}>
                <td className="px-3 py-2 font-semibold">{u.name}</td>
                <td className="px-3 py-2 text-slate-500">@{u.login_id}</td>
                <td className="px-3 py-2">{ROLE_LABEL[u.role]}</td>
                <td className="px-3 py-2">
                  {(u.capable_assignments || []).length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {u.capable_assignments.map((a) => (
                        <span key={a} className="text-[10px] bg-slate-100 text-slate-700 border border-slate-200 rounded-full px-1.5 py-0.5">{a}</span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">未設定</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span className={`text-xs rounded-full px-2 py-0.5 border ${
                    u.is_active ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-100 text-slate-500 border-slate-200"
                  }`}>{u.is_active ? "在籍" : "退職"}</span>
                </td>
                <td className="px-3 py-2 flex gap-1">
                  <Button data-testid={`edit-${u.id}`} size="sm" variant="outline" onClick={() => startEdit(u)} className="h-7">
                    <Edit2 className="w-3 h-3" />
                  </Button>
                  {u.role === "staff" && u.is_active && (
                    <Button
                      data-testid={`reset-pw-${u.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => setPwResetTarget(u)}
                      className="h-7 text-amber-700 border-amber-200 hover:bg-amber-50"
                      title="パスワード再発行"
                    >
                      <KeyRound className="w-3 h-3" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
        </TabsContent>

        <TabsContent value="assignments">
          <AssignmentManagement />
        </TabsContent>
      </Tabs>

      {/* 退職確認ダイアログ (shadcn AlertDialog) */}
      <AlertDialog open={!!confirmDeactivate} onOpenChange={(v) => !v && setConfirmDeactivate(null)}>
        <AlertDialogContent data-testid="deactivate-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>退職扱いにしますか？</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{confirmDeactivate?.name}</b> さんを退職扱いにします。<br />
              ※ 過去のシフト履歴は残ります。ログインはできなくなります。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deactivate-cancel">キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={doDeactivate} className="bg-rose-600 hover:bg-rose-700" data-testid="deactivate-confirm">退職扱いにする</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 完全削除 確認ダイアログ */}
      <AlertDialog open={!!confirmHardDelete} onOpenChange={(v) => !v && setConfirmHardDelete(null)}>
        <AlertDialogContent data-testid="hard-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-rose-700">本当に完全削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{confirmHardDelete?.name}</b>（@{confirmHardDelete?.login_id}）を<b className="text-rose-700">完全に削除</b>します。
              <div className="mt-3 p-3 bg-rose-50 border border-rose-200 rounded-md text-xs text-rose-900 leading-relaxed">
                ⚠️ この操作は <b>取り消せません</b>。<br />
                以下のデータも併せて削除されます：
                <ul className="list-disc pl-5 mt-1.5 space-y-0.5">
                  <li>このスタッフの希望提出（過去・未来すべて）</li>
                  <li>未確定の配置データ</li>
                  <li>このスタッフ宛・発のメッセージ</li>
                </ul>
                <div className="mt-2 text-[11px] text-rose-700">
                  ※ 確定済みシフトに含まれている場合は削除できません。
                  その場合は「退職扱い」をご利用ください。
                </div>
              </div>
              <div className="mt-3 text-xs text-slate-600">
                ログインIDを間違って登録した場合などに使用してください。
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="hard-delete-cancel">キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={doHardDelete} className="bg-rose-700 hover:bg-rose-800" data-testid="hard-delete-confirm">
              完全に削除する
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* PW再発行 確認ダイアログ */}
      <AlertDialog open={!!pwResetTarget} onOpenChange={(v) => !v && setPwResetTarget(null)}>
        <AlertDialogContent data-testid="reset-pw-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>パスワードを再発行しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{pwResetTarget?.name}</b> さんのパスワードを新しい10桁の英数字に変更します。<br />
              この操作は取り消せません。再発行後の新パスワードは次の画面で表示されますので、本人にお伝えください。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-pw-cancel">キャンセル</AlertDialogCancel>
            <AlertDialogAction
              onClick={doResetPassword}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="reset-pw-confirm"
            >
              再発行する
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PW再発行 結果ダイアログ / 新規スタッフ ログイン情報ダイアログ */}
      <Dialog open={!!pwResetResult} onOpenChange={(v) => !v && setPwResetResult(null)}>
        <DialogContent data-testid="reset-pw-result-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {pwResetResult?.isNew ? "スタッフのログイン情報" : "パスワードを再発行しました"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">
              <b>{pwResetResult?.display_name || pwResetResult?.login_id}</b> さん
              {pwResetResult?.isNew ? "の" : "の新しい"}ログイン情報です。<br />
              <span className="text-xs text-rose-600">この画面を閉じるとパスワードは再表示されません。必ず本人にお伝えください。</span>
            </p>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">企業ID</p>
                <code className="block font-mono text-sm font-bold text-slate-700">{actor?.login_id || "—"}</code>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">個人ID（ログインID）</p>
                <code className="block font-mono text-base font-bold text-slate-900">{pwResetResult?.login_id}</code>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  {pwResetResult?.isNew ? "パスワード" : "新しいパスワード"}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <code data-testid="reset-pw-value" className="flex-1 font-mono text-base font-bold text-slate-900 bg-white border border-slate-200 rounded px-2 py-1 break-all">
                    {pwResetResult?.password}
                  </code>
                  <Button
                    data-testid="reset-pw-copy"
                    size="sm"
                    variant="outline"
                    onClick={copyPw}
                    className="h-8 shrink-0"
                  >
                    <Copy className="w-3.5 h-3.5 mr-1" />コピー
                  </Button>
                </div>
              </div>
            </div>

            {/* LINE用案内文 プレビュー + コピー */}
            <div className="rounded-lg border-2 border-[#06C755]/40 bg-[#06C755]/5 p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-bold text-[#06C755] uppercase tracking-wider">
                  LINE / SMS用 案内文
                </p>
                <Button
                  data-testid="copy-line-message"
                  size="sm"
                  onClick={copyLineMessage}
                  className="h-7 bg-[#06C755] hover:bg-[#05A847] text-white"
                >
                  <MessageCircle className="w-3.5 h-3.5 mr-1" />
                  この文をコピー
                </Button>
              </div>
              <textarea
                readOnly
                value={buildLineMessage()}
                data-testid="line-message-preview"
                className="w-full text-[11px] font-mono bg-white border border-slate-200 rounded p-2 leading-relaxed resize-none"
                rows={11}
                onFocus={(e) => e.target.select()}
              />
              <p className="text-[10px] text-slate-500 mt-1.5 leading-relaxed">
                LINE や SMS でそのまま送れる文をコピーします。スタッフは「スタッフ用タブ」から企業ID＋個人ID＋パスワードでログインします。
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setPwResetResult(null)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="reset-pw-close"
            >
              閉じる
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
